import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ArrowLeft, FileText, Calendar } from "lucide-react";
import editaisPublicosService from "../../../services/editaisPublicosService";
import type { EditalPublico } from "../../../services/editaisPublicosService";

export default function ListaEditaisPublicos() {
  const [editais, setEditais] = useState<EditalPublico[]>([]);
  const [editaisFiltrados, setEditaisFiltrados] = useState<EditalPublico[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busca, setBusca] = useState("");
  const navigate = useNavigate();

  const carregarEditais = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await editaisPublicosService.listar({ busca });
      setEditais(response.editais);
      setEditaisFiltrados(response.editais);
    } catch (err) {
      setError("Erro ao carregar editais");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void carregarEditais();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let resultado = editais;

    // Filtrar por busca
    if (busca.trim()) {
      resultado = resultado.filter(
        (edital) =>
          edital.nome.toLowerCase().includes(busca.toLowerCase()) ||
          edital.descricao?.toLowerCase().includes(busca.toLowerCase())
      );
    }

    setEditaisFiltrados(resultado);
  }, [busca, editais]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
            <p className="mt-4 text-gray-600">Carregando editais...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6 lg:py-8 max-w-7xl">
        {/* Header com botão voltar */}
        <div className="mb-6 lg:mb-8">
          <button
            onClick={() => navigate(-1)}
            className="mb-4 inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Voltar</span>
          </button>

          <div>
            <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
              Editais Disponíveis
            </h1>
            <p className="text-gray-600">
              Lista completa de editais disponíveis para inscrição
            </p>
          </div>
        </div>

        {/* Busca */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6 border border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar por nome ou descrição do edital..."
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none"
            />
          </div>
        </div>

        {/* Total de editais */}
        <div className="mb-4">
          <p className="text-sm text-gray-600">
            {editaisFiltrados.length}{" "}
            {editaisFiltrados.length === 1
              ? "edital encontrado"
              : "editais encontrados"}
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        {/* Lista de Editais */}
        {editaisFiltrados.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center border border-gray-200">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600 text-lg font-medium mb-2">
              {busca
                ? "Nenhum edital encontrado com esses critérios"
                : "Não há editais disponíveis no momento"}
            </p>
            <p className="text-gray-500 text-sm">
              {busca
                ? "Tente ajustar a busca"
                : "Volte mais tarde para verificar novas oportunidades"}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5">
            {editaisFiltrados.map((edital) => (
              <div
                key={edital.id}
                className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-200"
              >
                <div className="p-5">
                  {/* Título e Badge de Status */}
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <h2 className="text-xl font-bold text-gray-900 flex-1">
                      {edital.nome}
                    </h2>
                    {edital.status_periodo && (
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                          edital.status_periodo === "aberto"
                            ? "bg-green-100 text-green-800"
                            : edital.status_periodo === "em_breve"
                              ? "bg-blue-100 text-blue-800"
                              : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {edital.status_periodo === "aberto"
                          ? "Aberto"
                          : edital.status_periodo === "em_breve"
                            ? "Em Breve"
                            : "Encerrado"}
                      </span>
                    )}
                  </div>

                  {/* Descrição */}
                  {edital.descricao && (
                    <p className="text-gray-600 mb-4 text-sm">
                      {edital.descricao}
                    </p>
                  )}

                  {/* Datas e Informações */}
                  <div className="space-y-3 mb-4 text-sm">
                    {/* Se ainda não abriu, mostrar apenas a data de abertura */}
                    {edital.status_periodo === "em_breve" ? (
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-blue-500" />
                        <div>
                          <span className="text-gray-500">Abertura: </span>
                          <span className="font-medium text-gray-900">
                            {new Date(edital.data_inicio).toLocaleDateString(
                              "pt-BR"
                            )}
                          </span>
                        </div>
                      </div>
                    ) : (
                      /* Se já abriu ou encerrou, mostrar início e término */
                      <div className="flex flex-wrap gap-4">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-gray-500" />
                          <div>
                            <span className="text-gray-500">Início: </span>
                            <span className="font-medium text-gray-900">
                              {new Date(edital.data_inicio).toLocaleDateString(
                                "pt-BR"
                              )}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-gray-500" />
                          <div>
                            <span className="text-gray-500">Término: </span>
                            <span className="font-medium text-gray-900">
                              {new Date(edital.data_fim).toLocaleDateString(
                                "pt-BR"
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Contador de dias */}
                    {edital.status_periodo === "aberto" &&
                      edital.dias_restantes !== undefined && (
                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-green-50 border border-green-200 rounded text-green-700 font-medium">
                          {edital.dias_restantes === 0 ? (
                            <span>Último dia para inscrição!</span>
                          ) : (
                            <span>
                              {edital.dias_restantes}{" "}
                              {edital.dias_restantes === 1
                                ? "dia restante"
                                : "dias restantes"}
                            </span>
                          )}
                        </div>
                      )}

                    {edital.status_periodo === "em_breve" &&
                      edital.dias_para_inicio !== undefined && (
                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 border border-blue-200 rounded text-blue-700 font-medium">
                          <span>
                            Abre em {edital.dias_para_inicio}{" "}
                            {edital.dias_para_inicio === 1 ? "dia" : "dias"}
                          </span>
                        </div>
                      )}

                    {/* Formação Mínima */}
                    {edital.formacao_minima_requerida && (
                      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-purple-50 border border-purple-200 rounded text-purple-700 text-xs">
                        <span className="font-medium">Formação mínima:</span>
                        <span className="capitalize">
                          {edital.formacao_minima_requerida
                            .replace("_", "-")
                            .replace("pos_doutorado", "Pós-Doutorado")
                            .replace("pos-doutorado", "Pós-Doutorado")
                            .replace("doutorado", "Doutorado")
                            .replace("mestrado", "Mestrado")
                            .replace("especializacao", "Especialização")
                            .replace("graduacao", "Graduação")}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Ações */}
                  <div className="pt-3 border-t border-gray-100">
                    <Link
                      to={`/editais/${edital.id}`}
                      className="inline-block bg-blue-600 text-white text-center px-6 py-2.5 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                    >
                      Ver Detalhes
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
